import moment from 'moment';
import { createAction } from '@reduxjs/toolkit';

import { getPartnerShortCode } from '@qwealth/qcore';
import { getAllLogs, updateLog, QLog, QTask, TASK_IN_PROGRESS, TASK_COMPLETED, QMessageMetadata } from '@qwealth/qdata';
import { getPathContents } from '@qwealth/qvault';

import { Activity } from '../models/Activity';
import { CalendarEvent, generateCalendarEvent, updateCalendarEvent, updateHouseholdCalendarEvent } from 'data/data-layer/event';
import { errorHandler } from 'services/axiosService';
import fetchFilteredEvents from './microsoft/helper';
import { getCallLogFilter, getMessageFilter, getTaskFilter, sortActivitiesByDate } from 'utils/activityFilterHelper';
import { HouseholdActivityFilters } from 'data/models/ActivityFilters';
import { fromS3ItemToMetadata } from '../models/QMessageMetadata';
import { AppThunkDispatch } from '../store';
import { deleteTaskAttachments } from './activities';
import { deleteTask, generateTask, getAllTasks, updateTask } from 'data/data-layer/qtask';
import { createCallLog, QLogCreatePayload } from 'data/data-layer/callLogs';
import { dispatch207Warning } from 'data/helpers/attachmentHelper';
import { ALL_DATE_RANGE } from 'data/refData/acitivites';

const PUT_HOUSEHOLD_ACTIVITY = 'PUT_HOUSEHOLD_ACTIVITY';
const PUT_HOUSEHOLD_ACTIVITIES = 'PUT_HOUSEHOLD_ACTIVITIES';
const DELETE_HOUSEHOLD_ACTIVITY = 'DELETE_HOUSEHOLD_ACTIVITY';
const RESET_HOUSEHOLD_ACTIVITIES = 'RESET_HOUSEHOLD_ACTIVITIES';
const SET_ACTIVITY_LOADING = 'SET_ACTIVITY_LOADING';
const DELETE_HOUSEHOLD_TASK = 'DELETE_HOUSEHOLD_TASK';
const CREATE_HOUSEHOLD_TASK = 'CREATE_HOUSEHOLD_TASK';
const UPDATE_HOUSEHOLD_TASK = 'UPDATE_HOUSEHOLD_TASK';

type ActivityId = {
  id?: number | string;
  uuid?: string;
};
export const deleteHouseholdActivity = createAction<ActivityId>(DELETE_HOUSEHOLD_ACTIVITY);
export const resetHouseholdActivities = createAction(RESET_HOUSEHOLD_ACTIVITIES);
export const putActivity = createAction<Activity>(PUT_HOUSEHOLD_ACTIVITY);
export const putActivities = createAction<Activity[]>(PUT_HOUSEHOLD_ACTIVITIES);
export const setLoading = createAction<boolean>(SET_ACTIVITY_LOADING);
export const createHouseholdTaskActivity = createAction<QTask[]>(CREATE_HOUSEHOLD_TASK);
export const updateHouseholdTaskActivity = createAction<QTask[]>(UPDATE_HOUSEHOLD_TASK);
export const deleteHouseholdTaskActivity = createAction<QTask[]>(DELETE_HOUSEHOLD_TASK);

const getEmails = (activityFilters: HouseholdActivityFilters): Promise<QMessageMetadata[]> => {
  const { householdQID } = activityFilters;
  const partnerShortCode = getPartnerShortCode(householdQID);
  const emailsPath = `${partnerShortCode}/${householdQID}/Internal/Emails/`;
  return getPathContents(emailsPath, true)
    .then(fileList => fileList.map(fromS3ItemToMetadata).filter(p => p.id !== emailsPath))
    .then(getMessageFilter(activityFilters))
    .catch(err => {
      console.error(err);
      return [];
    });
};

export const getHouseholdFilteredTasks = async (activityFilters: Omit<HouseholdActivityFilters, 'timeZone'>): Promise<QTask[]> => {
  try {
    return await getAllTasks(getTaskFilter(activityFilters));
  } catch (error) {
    console.error('Failed to load tasks:', error);
    return [];
  }
};

export const findHouseholdTaskById = async (taskId: number, householdId: string): Promise<QTask | undefined> => {
  const taskFilters: HouseholdActivityFilters = {
    sortDirection: 'DESC',
    filterDateValue: ALL_DATE_RANGE,
    filterActivities: ['tasks'],
    filterTaskResponsibilities: [],
    filterTaskStatus: [],
    filterTaskTags: [],
    filterTaskPriorities: [],
    filterLogSubject: '',
    filterDateRange: {
      fromDate: moment(),
      toDate: moment(),
    },
    timeZone: '',
    householdQID: householdId,
  };

  const tasks = await getHouseholdFilteredTasks(taskFilters);
  return tasks?.find(t => t.id === taskId);
};

export const loadActivities =
  (activityFilters: HouseholdActivityFilters) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    const { filterActivities } = activityFilters;
    dispatch(setLoading(true));
    dispatch(resetHouseholdActivities());

    const promises: Array<Promise<Array<Activity>>> = [];
    const hasEventType = filterActivities.includes('events');
    if (hasEventType && activityFilters.timeZone) {
      promises.push(fetchFilteredEvents(activityFilters));
    }

    const hasTaskType = filterActivities.includes('tasks');
    if (hasTaskType) {
      promises.push(getHouseholdFilteredTasks(activityFilters));
    }

    const hasLogType = filterActivities.includes('logs');
    if (hasLogType) {
      promises.push(getAllLogs(getCallLogFilter(activityFilters)));
    }

    const hasEmailType = filterActivities.includes('emails');
    if (hasEmailType) {
      promises.push(getEmails(activityFilters));
    }

    await Promise.all(promises)
      .then(results => results.reduce((all, current) => all.concat(current), []))
      .then(activities => activities.sort(sortActivitiesByDate(activityFilters.sortDirection)))
      .then(all => dispatch(putActivities(all)))
      .finally(() => dispatch(setLoading(false)));
  };

export const saveHouseholdTask =
  (task: QTask, activityFilters: Omit<HouseholdActivityFilters, 'timeZone'>) =>
  async (dispatch: AppThunkDispatch): Promise<QTask[] | null> =>
    task.id ? await dispatch(updateHouseholdTask(task, activityFilters)) : await dispatch(createHouseholdTask(task, activityFilters));

const createHouseholdTask =
  (task: QTask, activityFilters: Omit<HouseholdActivityFilters, 'timeZone'>) =>
  async (dispatch: AppThunkDispatch): Promise<QTask[] | null> => {
    dispatch(setLoading(true));

    try {
      const result = await generateTask(task);
      dispatch207Warning(result.message, dispatch);
      const getTasks = await getHouseholdFilteredTasks(activityFilters);
      dispatch(createHouseholdTaskActivity(getTasks));
      return result.data;
    } catch (error) {
      await errorHandler(dispatch, 'Failed to create a household task')(error);
      return null;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const updateHouseholdTask =
  (task: QTask, activityFilters: Omit<HouseholdActivityFilters, 'timeZone'>) =>
  async (dispatch: AppThunkDispatch): Promise<QTask[] | null> => {
    dispatch(setLoading(true));
    try {
      const updatedTask: QTask[] = await updateTask(task);
      const getTasks = await getHouseholdFilteredTasks(activityFilters);
      dispatch(updateHouseholdTaskActivity(getTasks));
      return updatedTask;
    } catch (error) {
      await errorHandler(dispatch, 'Failed to update a household task')(error);
      return null;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const completeHouseholdTask =
  (task: QTask) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    dispatch(setLoading(true));
    const completedTask: QTask = { ...task, status: TASK_COMPLETED };

    try {
      const updatedTask: QTask[] = await updateTask(completedTask);
      dispatch(updateHouseholdTaskActivity(updatedTask));
    } catch (error) {
      await errorHandler(dispatch, 'Failed to complete a household task')(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const deleteHouseholdTask =
  (task: QTask) =>
  async (dispatch: AppThunkDispatch): Promise<QTask | null> => {
    dispatch(setLoading(true));
    try {
      const deletedTasks: QTask[] = await deleteTask(task);
      dispatch(deleteHouseholdTaskActivity(deletedTasks));
      if (!('isRecurring' in task && task?.isRecurring)) {
        await deleteTaskAttachments(task);
      }
      if (task.isRecurring) {
        return deletedTasks.find(deletedTask => deletedTask.id === task.id) || null;
      }
      return deletedTasks[0] || null;
    } catch (error) {
      await errorHandler(dispatch, 'Failed to deleted a household task')(error);
      return null;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const resetHouseholdTask =
  (task: QTask) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    dispatch(setLoading(true));
    const resetTask: QTask = { ...task, status: TASK_IN_PROGRESS };

    try {
      const updatedTask: QTask[] = await updateTask(resetTask);
      dispatch(updateHouseholdTaskActivity(updatedTask));
    } catch (error) {
      await errorHandler(dispatch, 'Failed to reset a household task')(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

const createHouseholdLog =
  (qLog: QLogCreatePayload) =>
  async (dispatch: AppThunkDispatch): Promise<QLog | null> => {
    dispatch(setLoading(true));
    try {
      const result = await createCallLog(qLog);
      dispatch207Warning(result.message, dispatch);
      dispatch(putActivity(result.data));
      return result.data;
    } catch (error) {
      await errorHandler(dispatch, 'Could not create household log')(error);
      return null;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const saveHouseholdLog = (callLog: QLog | QLogCreatePayload) => async (dispatch: AppThunkDispatch) =>
  callLog.uuid ? await dispatch(updateHouseholdLog(callLog as QLog)) : await dispatch(createHouseholdLog(callLog as QLogCreatePayload));

const updateHouseholdLog =
  (callLog: QLog) =>
  async (dispatch: AppThunkDispatch): Promise<QLog | null> => {
    dispatch(setLoading(true));
    try {
      const updatedCallLog = await updateLog(callLog);
      dispatch(putActivity(updatedCallLog));
      return updatedCallLog;
    } catch (error) {
      await errorHandler(dispatch, 'Failed to update household log')(error);
      return null;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const createHouseholdEvent =
  (event, householdId: string, fromCalendarId) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    dispatch(setLoading(true));

    try {
      const postedEvent = await generateCalendarEvent(event, fromCalendarId);
      const update = {
        singleValueExtendedProperties: [
          {
            id: 'String {00020329-0000-0000-C000-000000000046} Name HouseholdId',
            value: householdId,
          },
        ],
      };

      await updateHouseholdCalendarEvent(update, postedEvent.id!);

      dispatch(putActivity(postedEvent));
    } catch (error) {
      await errorHandler(dispatch)(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const updateHouseholdEvent =
  event =>
  async (dispatch: AppThunkDispatch): Promise<CalendarEvent | null> => {
    dispatch(setLoading(true));

    try {
      const patchedEvent = await updateCalendarEvent(event, event.id);
      dispatch(putActivity(patchedEvent));
      return patchedEvent;
    } catch (error) {
      await errorHandler(dispatch)(error);
      return null;
    } finally {
      dispatch(setLoading(false));
    }
  };
